import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ShoppingCart, ArrowLeft, Heart, Star, Trash2, ZoomIn, BellRing } from 'lucide-react';
import { AppContext } from '../App';
import { toast } from 'sonner';
import { trackViewItem, trackAddToCart } from '../utils/analytics';
import { productUrl } from '../utils/slug';
import { getImageUrl } from '../utils/image';
import ImageLightbox from '../components/ImageLightbox';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const translations = {
  en: {
    addToCart: 'Add to Cart',
    adding: 'Adding...',
    outOfStock: 'Out of Stock',
    condition: 'Condition',
    category: 'Category',
    boughtTogether: 'Frequently Bought Together',
    boughtTogetherDesc: 'Customers who bought this also purchased',
    addAllToCart: 'Add All to Cart',
    youMayLike: 'Recommended for You',
    youMayLikeDesc: 'Similar stamps and FDCs from our collection',
    recentlyViewed: 'Recently Viewed',
    recentlyViewedDesc: 'Products you browsed earlier',
    addedToCart: 'Added to cart successfully',
    loginToOrder: 'Please login to add items to cart',
    buyNow: 'Buy Now',
    processing: 'Processing...',
    addressRequired: 'Shipping Address Required',
    addressRequiredDesc: 'Please provide your shipping address to continue with express checkout.',
    fullName: 'Full Name',
    email: 'Email',
    address: 'Address',
    city: 'City',
    country: 'Country',
    zipCode: 'Zip Code',
    phone: 'Phone (optional)',
    cancel: 'Cancel',
    saveAndContinue: 'Save & Continue',
    buyNowError: 'Failed to process. Please try again.',
    notifyMe: 'Notify Me When Available',
    notifyPlaceholder: 'Enter your email',
    notifySuccess: 'We\'ll notify you when this item is back in stock!',
    notifyAlready: 'You\'re already subscribed for this item.',
    reviews: 'Customer Reviews',
    noReviews: 'No reviews yet. Be the first to share your experience!',
    writeReview: 'Write a Review',
    reviewTitle: 'Review Title',
    reviewComment: 'Your Review',
    submitReview: 'Submit Review',
    loginToReview: 'Please login to leave a review',
    reviewSubmitted: 'Review submitted!',
    alreadyReviewed: 'You have already reviewed this product',
    rating: 'Rating',
    deleteReview: 'Delete',
    priceDropNotify: 'Notify Me if Price Drops',
    priceDropEmail: 'Enter your email',
    priceDropSuccess: 'We\'ll email you when the price drops!',
    priceDropAlready: 'You\'re already subscribed for price alerts.'
  },
  tr: {
    addToCart: 'Sepete Ekle',
    adding: 'Ekleniyor...',
    outOfStock: 'Stokta Yok',
    condition: 'Durum',
    category: 'Kategori',
    boughtTogether: 'Birlikte Sıkça Alınanlar',
    boughtTogetherDesc: 'Bu ürünü alan müşteriler bunları da satın aldı',
    addAllToCart: 'Tümünü Sepete Ekle',
    youMayLike: 'Sizin İçin Önerilen',
    youMayLikeDesc: 'Koleksiyonumuzdan benzer pullar ve FDC\'ler',
    recentlyViewed: 'Son Görüntülenen',
    recentlyViewedDesc: 'Daha önce incelediğiniz ürünler',
    addedToCart: 'Sepete başarıyla eklendi',
    loginToOrder: 'Ürün eklemek için lütfen giriş yapın',
    buyNow: 'Hemen Satın Al',
    processing: 'İşleniyor...',
    addressRequired: 'Teslimat Adresi Gerekli',
    addressRequiredDesc: 'Hızlı ödeme ile devam etmek için lütfen teslimat adresinizi girin.',
    fullName: 'Ad Soyad',
    email: 'E-posta',
    address: 'Adres',
    city: 'Şehir',
    country: 'Ülke',
    zipCode: 'Posta Kodu',
    phone: 'Telefon (isteğe bağlı)',
    cancel: 'İptal',
    saveAndContinue: 'Kaydet ve Devam Et',
    buyNowError: 'İşlem başarısız. Lütfen tekrar deneyin.',
    notifyMe: 'Stoka Gelince Haber Ver',
    notifyPlaceholder: 'E-posta adresinizi girin',
    notifySuccess: 'Bu ürün stoka geldiğinde size haber vereceğiz!',
    notifyAlready: 'Bu ürün için zaten kayıtlısınız.',
    reviews: 'Müşteri Yorumları',
    noReviews: 'Henüz yorum yok. Deneyiminizi paylaşan ilk siz olun!',
    writeReview: 'Yorum Yaz',
    reviewTitle: 'Yorum Başlığı',
    reviewComment: 'Yorumunuz',
    submitReview: 'Gönder',
    loginToReview: 'Yorum yapmak için lütfen giriş yapın',
    reviewSubmitted: 'Yorum gönderildi!',
    alreadyReviewed: 'Bu ürünü zaten değerlendirdiniz',
    rating: 'Puan',
    deleteReview: 'Sil',
    priceDropNotify: 'Fiyat Düşerse Haber Ver',
    priceDropEmail: 'E-posta adresinizi girin',
    priceDropSuccess: 'Fiyat düştüğünde size e-posta göndereceğiz!',
    priceDropAlready: 'Fiyat uyarısı için zaten kayıtlısınız.'
  },
  el: {
    addToCart: 'Προσθήκη στο Καλάθι',
    adding: 'Προσθήκη...',
    outOfStock: 'Εξαντλημένο',
    condition: 'Κατάσταση',
    category: 'Κατηγορία',
    boughtTogether: 'Συχνά Αγοράζονται Μαζί',
    boughtTogetherDesc: 'Οι πελάτες που αγόρασαν αυτό αγόρασαν επίσης',
    addAllToCart: 'Προσθήκη Όλων',
    youMayLike: 'Προτεινόμενα για Εσάς',
    youMayLikeDesc: 'Παρόμοια γραμματόσημα και FDC από τη συλλογή μας',
    recentlyViewed: 'Πρόσφατα Προβεβλημένα',
    recentlyViewedDesc: 'Προϊόντα που είδατε πρόσφατα',
    addedToCart: 'Προστέθηκε επιτυχώς στο καλάθι',
    loginToOrder: 'Παρακαλώ συνδεθείτε για να προσθέσετε προϊόντα',
    buyNow: 'Αγορά Τώρα',
    processing: 'Επεξεργασία...',
    addressRequired: 'Απαιτείται Διεύθυνση Αποστολής',
    addressRequiredDesc: 'Παρακαλώ δώστε τη διεύθυνση αποστολής σας για να συνεχίσετε.',
    fullName: 'Ονοματεπώνυμο',
    email: 'Email',
    address: 'Διεύθυνση',
    city: 'Πόλη',
    country: 'Χώρα',
    zipCode: 'Τ.Κ.',
    phone: 'Τηλέφωνο (προαιρετικό)',
    cancel: 'Ακύρωση',
    saveAndContinue: 'Αποθήκευση & Συνέχεια',
    buyNowError: 'Αποτυχία επεξεργασίας. Δοκιμάστε ξανά.',
    notifyMe: 'Ειδοποίηση Διαθεσιμότητας',
    notifyPlaceholder: 'Εισάγετε το email σας',
    notifySuccess: 'Θα σας ειδοποιήσουμε όταν είναι ξανά διαθέσιμο!',
    notifyAlready: 'Είστε ήδη εγγεγραμμένοι.',
    reviews: 'Κριτικές Πελατών',
    noReviews: 'Δεν υπάρχουν κριτικές ακόμα. Γίνετε ο πρώτος!',
    writeReview: 'Γράψτε μια Κριτική',
    reviewTitle: 'Τίτλος Κριτικής',
    reviewComment: 'Η Κριτική σας',
    submitReview: 'Υποβολή',
    loginToReview: 'Συνδεθείτε για να αφήσετε κριτική',
    reviewSubmitted: 'Η κριτική υποβλήθηκε!',
    alreadyReviewed: 'Έχετε ήδη αξιολογήσει αυτό το προϊόν',
    rating: 'Βαθμολογία',
    deleteReview: 'Διαγραφή',
    priceDropNotify: 'Ειδοποίηση Μείωσης Τιμής',
    priceDropEmail: 'Εισάγετε το email σας',
    priceDropSuccess: 'Θα σας ειδοποιήσουμε όταν πέσει η τιμή!',
    priceDropAlready: 'Είστε ήδη εγγεγραμμένοι.'
  },
  de: {
    addToCart: 'In den Warenkorb',
    adding: 'Wird hinzugefügt...',
    outOfStock: 'Ausverkauft',
    condition: 'Zustand',
    category: 'Kategorie',
    boughtTogether: 'Häufig Zusammen Gekauft',
    boughtTogetherDesc: 'Kunden, die dies kauften, kauften auch',
    addAllToCart: 'Alle in den Warenkorb',
    youMayLike: 'Empfohlen für Sie',
    youMayLikeDesc: 'Ähnliche Briefmarken und FDCs aus unserer Sammlung',
    recentlyViewed: 'Kürzlich Angesehen',
    recentlyViewedDesc: 'Produkte, die Sie zuvor angesehen haben',
    addedToCart: 'Erfolgreich zum Warenkorb hinzugefügt',
    loginToOrder: 'Bitte melden Sie sich an, um Artikel hinzuzufügen',
    buyNow: 'Jetzt Kaufen',
    processing: 'Verarbeitung...',
    addressRequired: 'Lieferadresse Erforderlich',
    addressRequiredDesc: 'Bitte geben Sie Ihre Lieferadresse ein, um fortzufahren.',
    fullName: 'Vollständiger Name',
    email: 'E-Mail',
    address: 'Adresse',
    city: 'Stadt',
    country: 'Land',
    zipCode: 'PLZ',
    phone: 'Telefon (optional)',
    cancel: 'Abbrechen',
    saveAndContinue: 'Speichern & Weiter',
    buyNowError: 'Verarbeitung fehlgeschlagen. Bitte erneut versuchen.',
    notifyMe: 'Benachrichtigen bei Verfügbarkeit',
    notifyPlaceholder: 'E-Mail eingeben',
    notifySuccess: 'Wir benachrichtigen Sie, wenn der Artikel wieder verfügbar ist!',
    notifyAlready: 'Sie sind bereits angemeldet.',
    reviews: 'Kundenbewertungen',
    noReviews: 'Noch keine Bewertungen. Seien Sie der Erste!',
    writeReview: 'Bewertung Schreiben',
    reviewTitle: 'Bewertungstitel',
    reviewComment: 'Ihre Bewertung',
    submitReview: 'Absenden',
    loginToReview: 'Bitte melden Sie sich an, um eine Bewertung abzugeben',
    reviewSubmitted: 'Bewertung eingereicht!',
    alreadyReviewed: 'Sie haben dieses Produkt bereits bewertet',
    rating: 'Bewertung',
    deleteReview: 'Löschen',
    priceDropNotify: 'Benachrichtigung bei Preissenkung',
    priceDropEmail: 'E-Mail eingeben',
    priceDropSuccess: 'Wir benachrichtigen Sie bei einer Preissenkung!',
    priceDropAlready: 'Sie sind bereits angemeldet.'
  }
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchCart, wishlistIds, toggleWishlist, language, user, openCartDrawer } = useContext(AppContext);
  const t = translations[language] || translations.en;
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifySubmitted, setNotifySubmitted] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, total: 0 });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, title: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [boughtTogether, setBoughtTogether] = useState([]);
  const [priceDropEmail, setPriceDropEmail] = useState('');
  const [priceDropSubmitted, setPriceDropSubmitted] = useState(false);
  const [showPriceDrop, setShowPriceDrop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowStickyBar(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Buy Now states
  const [buyingNow, setBuyingNow] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressData, setAddressData] = useState({
    full_name: '',
    email: '',
    address: '',
    city: '',
    country: '',
    zip_code: '',
    phone: ''
  });

  useEffect(() => {
    fetchProduct();
  }, [id]);

  // Load recently viewed products and save current product to history
  useEffect(() => {
    if (!product) return;
    try {
      const stored = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
      // Remove current product if already in list, then prepend
      const updated = [id, ...stored.filter(pid => pid !== id)].slice(0, 10);
      localStorage.setItem('recentlyViewed', JSON.stringify(updated));
      // Fetch the other recently viewed products (excluding current)
      const otherIds = updated.filter(pid => pid !== id).slice(0, 4);
      if (otherIds.length > 0) {
        Promise.all(otherIds.map(pid =>
          axios.get(`${API}/products/${pid}`).then(r => r.data).catch(() => null)
        )).then(results => {
          setRecentlyViewed(results.filter(p => p && !p.deleted && !p.archived));
        });
      }
    } catch (e) {
      // silently ignore localStorage errors
    }
  }, [product, id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      trackViewItem(response.data);

      // Track view for popularity
      axios.post(`${API}/products/${id}/view`).catch(() => {});

      const relatedResponse = await axios.get(`${API}/products?category=${response.data.category}&limit=20&active_only=true`);
      const relatedData = relatedResponse.data.products || relatedResponse.data;
      const filteredRelated = Array.isArray(relatedData) 
        ? relatedData.filter(p => p.id !== id).slice(0, 3)
        : [];
      
      setRelatedProducts(filteredRelated);

      axios.get(`${API}/products/${id}/bought-together?limit=4`).then(res => {
        setBoughtTogether(Array.isArray(res.data) ? res.data : []);
      }).catch(() => {});
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}/reviews`);
      setReviews(response.data.reviews);
      setReviewStats({ average_rating: response.data.average_rating, total: response.data.total });
    } catch (error) {
      console.debug('Reviews fetch skipped');
    }
  };

  useEffect(() => {
    if (id) fetchReviews();
  }, [id]);

  const handleSubmitReview = async () => {
    if (!user) { toast.error(t.loginToReview); return; }
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/products/${id}/reviews`, reviewData, { withCredentials: true });
      toast.success(t.reviewSubmitted);
      setShowReviewForm(false);
      setReviewData({ rating: 5, title: '', comment: '' });
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.alreadyReviewed);
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, { withCredentials: true });
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const [addingToCart, setAddingToCart] = useState(false);
  const handleAddToCart = async () => {
    if (addingToCart) return;
    setAddingToCart(true);
    try {
      const session_id = document.cookie.replace(/(?:(?:^|.*;\s*)cart_session\s*=\s*([^;]*).*$)|^.*$/, "$1") || null;

      await axios.post(`${API}/cart/add`, null, {
        params: { product_id: id, quantity },
        withCredentials: true,
        headers: session_id ? { 'X-Session-Id': session_id } : {}
      });

      if (product) trackAddToCart(product, quantity);
      await fetchCart();
      openCartDrawer();
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  // Buy Now handler
  const handleBuyNow = async (withAddress = false) => {
    setBuyingNow(true);
    
    try {
      const requestData = {
        quantity: quantity,
        ...(withAddress && {
          email: addressData.email,
          full_name: addressData.full_name,
          address: addressData.address,
          city: addressData.city,
          country: addressData.country,
          zip_code: addressData.zip_code,
          phone: addressData.phone
        })
      };
      
      const response = await axios.post(
        `${API}/products/${id}/buy-now`,
        requestData,
        { withCredentials: true }
      );
      
      if (response.data.status === 'needs_address') {
        // Pre-fill from user data and try to load saved address
        try {
          const addrRes = await axios.get(`${API}/user/address`, { withCredentials: true });
          if (addrRes.data) {
            setAddressData({
              full_name: addrRes.data.full_name || user?.name || '',
              email: addrRes.data.email || user?.email || '',
              address: addrRes.data.address || '',
              city: addrRes.data.city || '',
              country: addrRes.data.country || '',
              zip_code: addrRes.data.zip_code || '',
              phone: addrRes.data.phone || ''
            });
          } else {
            setAddressData(prev => ({ ...prev, email: user?.email || '', full_name: user?.name || '' }));
          }
        } catch { setAddressData(prev => ({ ...prev, email: user?.email || '', full_name: user?.name || '' })); }
        setShowAddressModal(true);
      } else if (response.data.status === 'success') {
        // Redirect to express checkout
        navigate(response.data.redirect_url);
      }
    } catch (error) {
      console.error('Buy Now error:', error);
      toast.error(error.response?.data?.detail || t.buyNowError);
    } finally {
      setBuyingNow(false);
    }
  };

  const handleAddressSaveAndContinue = async () => {
    // Validate required fields
    if (!addressData.full_name || !addressData.email || !addressData.address || !addressData.city || !addressData.country) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setShowAddressModal(false);
    await handleBuyNow(true);
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse">
            <div className="bg-gray-200 rounded-lg h-96"></div>
            <div className="space-y-4 py-4">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-10 bg-gray-200 rounded w-1/3 mt-6"></div>
              <div className="space-y-2 mt-6">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              </div>
              <div className="flex gap-3 mt-8">
                <div className="h-12 bg-gray-200 rounded flex-1"></div>
                <div className="h-12 bg-gray-200 rounded flex-1"></div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold mb-4">Product Not Found</h2>
          <button onClick={() => navigate('/shop')} className="btn-primary">
            Back to Shop
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const displayTitle = product[`title_${language}`] || product.title;
  const displayDescription = product[`description_${language}`] || product.description;
  const firstImage = product.images && product.images.length > 0 
    ? (product.images[0].startsWith('http') ? product.images[0] : `${BACKEND_URL}${product.images[0]}`)
    : '';
  const canonicalProductUrl = `https://www.northcyprusphilately.com${productUrl(product)}`;

  // Extract year and postmark from title or custom fields
  const issueYear = product.year || product.issue_year || '';
  const postmarkDate = product.postmark_date || '';
  
  // Use backend SEO fields if admin has set them, otherwise auto-generate
  const seoTitle = product.meta_title || [
    displayTitle,
    issueYear && `(${issueYear})`,
    product.condition,
    `$${product.price}`,
    '| North Cyprus Philately'
  ].filter(Boolean).join(' ');

  const seoDescription = product.meta_description
    || (displayDescription && displayDescription.length > 20
      ? displayDescription.substring(0, 155).replace(/\s+\S*$/, '...')
      : [
          `Buy ${displayTitle}`,
          product.condition && `(${product.condition})`,
          issueYear && `from ${issueYear}`,
          `- $${product.price}.`,
          product.stock > 0 ? 'In stock.' : 'Sold out.',
          'Free shipping on $100+ orders. North Cyprus Philately.'
        ].filter(Boolean).join(' '));

  return (
    <div className="product-detail-page">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={`${product.category}, ${displayTitle}, ${issueYear}, stamps, philately, TRNC, North Cyprus, first day cover, ${product.condition}`}
        image={firstImage}
        url={canonicalProductUrl}
        type="product"
        lang={language}
      />
      
      {/* Schema.org Product Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "Product",
          "name": displayTitle,
          "image": firstImage,
          "description": displayDescription || displayTitle,
          "sku": product.id,
          "brand": {
            "@type": "Brand",
            "name": "North Cyprus Philately"
          },
          "offers": {
            "@type": "Offer",
            "url": canonicalProductUrl,
            "priceCurrency": product.currency || "USD",
            "price": product.price,
            "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "seller": {
              "@type": "Organization",
              "name": "North Cyprus Philately"
            }
          },
          "category": product.category,
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Condition",
              "value": product.condition || "Mint"
            },
            {
              "@type": "PropertyValue",
              "name": "Type",
              "value": "First Day Cover"
            },
            issueYear && {
              "@type": "PropertyValue",
              "name": "Issue Year",
              "value": issueYear
            },
            postmarkDate && {
              "@type": "PropertyValue",
              "name": "Postmark Date",
              "value": postmarkDate
            },
            {
              "@type": "PropertyValue",
              "name": "SKU",
              "value": product.sku || product.id
            }
          ].filter(Boolean),
          ...(reviewStats.total > 0 ? {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": reviewStats.average_rating.toFixed(1),
              "reviewCount": reviewStats.total
            }
          } : {}),
          ...(reviews.length > 0 ? {
            "review": reviews.slice(0, 10).map(r => ({
              "@type": "Review",
              "reviewRating": {
                "@type": "Rating",
                "ratingValue": r.rating,
                "bestRating": 5
              },
              "author": {
                "@type": "Person",
                "name": r.user_name || "Customer"
              },
              ...(r.title ? { "name": r.title } : {}),
              ...(r.comment ? { "reviewBody": r.comment } : {}),
              ...(r.created_at ? { "datePublished": r.created_at.split('T')[0] } : {})
            }))
          } : {})
        })}
      </script>

      {/* BreadcrumbList Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.northcyprusphilately.com/" },
            { "@type": "ListItem", "position": 2, "name": "Shop", "item": "https://www.northcyprusphilately.com/shop" },
            ...(product.categories?.[0] ? [{ "@type": "ListItem", "position": 3, "name": product.categories[0].replace(/-/g, ' '), "item": `https://www.northcyprusphilately.com/shop?category=${product.categories[0]}` }] : []),
            { "@type": "ListItem", "position": product.categories?.[0] ? 4 : 3, "name": displayTitle }
          ]
        })}
      </script>
      <Header />

      {lightboxOpen && product?.images?.length > 0 && (
        <ImageLightbox
          images={product.images}
          initialIndex={selectedImageIdx || 0}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {/* Sticky Buy Bar */}
      {showStickyBar && product && product.stock > 0 && (
        <div className="fixed top-0 left-0 right-0 bg-white shadow-md z-40 border-b">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {firstImage && <img src={firstImage} alt="" className="w-10 h-10 object-cover rounded hidden sm:block" />}
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{displayTitle}</p>
                <p className="text-[#B8941E] font-bold text-sm">
                  {product.discounted_price && product.discounted_price < product.price
                    ? <><span>${product.discounted_price.toFixed(2)}</span> <span className="text-gray-400 line-through text-xs">${product.price.toFixed(2)}</span></>
                    : `$${product.price.toFixed(2)}`
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => handleBuyNow(false)} className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1">
                ⚡ {language === 'tr' ? 'Hemen Al' : 'Buy Now'}
              </button>
              <button onClick={handleAddToCart} className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1">
                <ShoppingCart size={16} /> {language === 'tr' ? 'Sepete Ekle' : 'Add to Cart'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shipping & Discount Banner */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-3 text-xs md:text-sm">
            <span>🚢 $4 Shipping</span>
            <span>|</span>
            <span>✓ Free $100+</span>
            <span>|</span>
            <span>🎁 10% Off $100+</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex items-center gap-2 text-sm text-gray-500">
            <li><Link to="/" className="hover:text-[#C8A951] transition">{language === 'tr' ? 'Ana Sayfa' : 'Home'}</Link></li>
            <li>/</li>
            <li><Link to="/shop" className="hover:text-[#C8A951] transition">{language === 'tr' ? 'Mağaza' : 'Shop'}</Link></li>
            {product.categories && product.categories[0] && (
              <>
                <li>/</li>
                <li><Link to={`/shop?category=${product.categories[0]}`} className="hover:text-[#C8A951] transition capitalize">{product.categories[0].replace(/-/g, ' ')}</Link></li>
              </>
            )}
            <li>/</li>
            <li className="text-gray-800 font-medium truncate max-w-xs">{displayTitle}</li>
          </ol>
        </nav>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              {product.images && product.images.length > 0 && (
                <div className="space-y-3">
                  {/* Main Image with Zoom */}
                  <div
                    className="relative group overflow-hidden rounded-lg shadow-md cursor-zoom-in"
                    onClick={() => setLightboxOpen(true)}
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = ((e.clientX - rect.left) / rect.width) * 100;
                      const y = ((e.clientY - rect.top) / rect.height) * 100;
                      const img = e.currentTarget.querySelector('img');
                      if (img) { img.style.transformOrigin = `${x}% ${y}%`; }
                    }}
                    onMouseEnter={(e) => {
                      const img = e.currentTarget.querySelector('img');
                      if (img) img.style.transform = 'scale(2)';
                    }}
                    onMouseLeave={(e) => {
                      const img = e.currentTarget.querySelector('img');
                      if (img) { img.style.transform = 'scale(1)'; img.style.transformOrigin = 'center'; }
                    }}
                  >
                    <img
                      src={getImageUrl(product.images[selectedImageIdx || 0] || product.images[0])}
                      alt={`${displayTitle} - ${product.condition || 'collectible'} stamp from North Cyprus (TRNC)`}
                      className="w-full h-auto transition-transform duration-200"
                      width="600"
                      height="600"
                      sizes="(max-width: 768px) 100vw, 50vw"
                      decoding="async"
                      fetchpriority="high"
                      onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/500x500?text=No+Image'; }}
                    />
                    <div className="absolute top-3 right-3 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition pointer-events-none">
                      <ZoomIn size={18} />
                    </div>
                  </div>
                  {/* Thumbnail Gallery */}
                  {product.images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {product.images.map((img, idx) => {
                        const src = img.startsWith('http') ? img : `${BACKEND_URL}${img}`;
                        return (
                          <button
                            key={idx}
                            onClick={() => setSelectedImageIdx(idx)}
                            className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition ${(selectedImageIdx || 0) === idx ? 'border-[#C8A951]' : 'border-transparent hover:border-gray-300'}`}
                          >
                            <img src={src} alt={`${displayTitle} - Image ${idx + 1}`} className="w-full h-full object-cover" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <h1 className="text-3xl font-bold mb-4">{displayTitle}</h1>
              <div className="mb-4">
                {product.discounted_price && product.discounted_price < product.price ? (
                  <div className="flex items-center gap-3">
                    <p className="text-2xl font-semibold text-[#C8A951]">
                      {product.currency} {product.discounted_price.toFixed(2)}
                    </p>
                    <p className="text-lg text-gray-400 line-through">
                      {product.currency} {product.price.toFixed(2)}
                    </p>
                    {product.discount_rate > 0 && (
                      <span className="bg-red-500 text-white text-sm font-bold px-2 py-1 rounded">
                        -{product.discount_rate}% OFF
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-2xl font-semibold text-[#C8A951]">
                    {product.currency} {product.price.toFixed(2)}
                  </p>
                )}
              </div>
              {product.stock > 0 && product.stock <= 5 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2 mb-4 inline-flex items-center gap-2">
                  <span className="text-orange-600 font-semibold text-sm animate-pulse">
                    Only {product.stock} left in stock - order soon!
                  </span>
                </div>
              )}

              {product.condition && (
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-semibold">{t.condition}:</span> {product.condition}
                </p>
              )}
              
              {product.category && (
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-semibold">{t.category}:</span> {product.category}
                </p>
              )}

              <p className="text-gray-700 mb-6 leading-relaxed">
                {displayDescription}
              </p>

              {product.stock > 0 ? (
                <div className="mb-6">
                  <label className="block font-semibold mb-2">Quantity:</label>
                  <div className="flex items-center gap-4">
                    <input 
                      type="number" 
                      min="1" 
                      max={product.stock}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock, parseInt(e.target.value) || 1)))}
                      className="w-20 text-center"
                    />
                    <span className="text-sm text-gray-600">{product.stock} available</span>
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <span className="text-red-600 font-semibold">{t.outOfStock}</span>
                  {!notifySubmitted ? (
                    <form
                      className="mt-3 flex gap-2"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          await axios.post(`${API}/stock-notify?product_id=${id}&email=${encodeURIComponent(notifyEmail)}`);
                          setNotifySubmitted(true);
                          toast.success(t.notifySuccess);
                        } catch (error) {
                          toast.info(t.notifyAlready);
                        }
                      }}
                    >
                      <input
                        type="email"
                        required
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        placeholder={t.notifyPlaceholder}
                        className="flex-1 px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                      />
                      <button type="submit" className="btn-primary text-sm whitespace-nowrap">
                        {t.notifyMe}
                      </button>
                    </form>
                  ) : (
                    <p className="mt-3 text-sm text-green-600">{t.notifySuccess}</p>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-2">
                {/* PRIMARY: Buy Now */}
                <button
                  onClick={() => handleBuyNow(false)}
                  disabled={product.stock === 0 || buyingNow}
                  className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {buyingNow ? (
                    <>
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                      {t.processing}
                    </>
                  ) : (
                    <>⚡ {t.buyNow}</>
                  )}
                </button>

                {/* SECONDARY: Add to Cart + Wishlist */}
                <div className="flex gap-2">
                  <button
                    onClick={handleAddToCart}
                    disabled={product.stock === 0 || addingToCart}
                    className="btn-secondary flex-1 flex items-center justify-center gap-2"
                  >
                    {addingToCart ? <div className="animate-spin h-5 w-5 border-2 border-gray-600 border-t-transparent rounded-full"></div> : <ShoppingCart size={18} />}
                    {t.addToCart}
                  </button>
                  <button
                    onClick={() => toggleWishlist(id)}
                    className="p-3 border rounded-lg hover:bg-red-50 transition"
                    aria-label="Toggle wishlist"
                  >
                    <Heart size={20} className={wishlistIds.includes(id) ? 'fill-red-500 text-red-500' : 'text-gray-400'} />
                  </button>
                </div>

                {/* No account needed notice */}
                <p className="text-xs text-center text-gray-500 mt-1">
                  {language === 'tr' ? '✅ Hesap gerekmez — misafir olarak satın alabilirsiniz' :
                   language === 'de' ? '✅ Kein Konto nötig — als Gast kaufen' :
                   language === 'el' ? '✅ Χωρίς λογαριασμό — αγορά ως επισκέπτης' :
                   '✅ No account needed — checkout as a guest'}
                </p>
              </div>

              {/* Social Share */}
              <div className="mt-4 flex items-center gap-3">
                <span className="text-sm text-gray-500">{language === 'tr' ? 'Paylaş:' : language === 'de' ? 'Teilen:' : language === 'el' ? 'Μοιραστείτε:' : 'Share:'}</span>
                <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(displayTitle + ' - ' + window.location.href)}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-green-500 text-white hover:bg-green-600 transition" aria-label="Share on WhatsApp">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalProductUrl)}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition" aria-label="Share on Facebook">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
                </a>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(canonicalProductUrl)}&text=${encodeURIComponent(displayTitle)}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-black text-white hover:bg-gray-800 transition" aria-label="Share on X">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(canonicalProductUrl)}&media=${encodeURIComponent(firstImage)}&description=${encodeURIComponent(displayTitle)}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition" aria-label="Share on Pinterest">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
                </a>
                <button
                  onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success(language === 'tr' ? 'Link kopyalandı!' : 'Link copied!'); }}
                  className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 transition"
                  aria-label="Copy link"
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                </button>
                <button
                  onClick={() => window.print()}
                  className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 transition"
                  aria-label="Print"
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                </button>
              </div>

              {/* Shipping Info */}
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm text-green-800">
                  <span>🚢</span>
                  <span className="font-semibold">{language === 'tr' ? 'Kargo: Taahhütlü posta sadece $4 | $100+ siparişlerde ücretsiz' : 'Shipping: Registered mail only $4 | Free on orders $100+'}</span>
                </div>
              </div>

              {/* Price Drop Notification */}
              {product.stock > 0 && (
                <div className="mt-3">
                  {!showPriceDrop && !priceDropSubmitted && (
                    <button
                      onClick={() => setShowPriceDrop(true)}
                      className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#B8941E] transition"
                    >
                      <BellRing size={16} />
                      {t.priceDropNotify}
                    </button>
                  )}
                  {showPriceDrop && !priceDropSubmitted && (
                    <form
                      className="flex gap-2"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          const res = await axios.post(`${API}/price-drop-notify?product_id=${id}&email=${encodeURIComponent(priceDropEmail)}`);
                          if (res.data.already) {
                            toast.info(t.priceDropAlready);
                          } else {
                            toast.success(t.priceDropSuccess);
                          }
                          setPriceDropSubmitted(true);
                        } catch {
                          toast.info(t.priceDropAlready);
                        }
                      }}
                    >
                      <input
                        type="email"
                        required
                        value={priceDropEmail}
                        onChange={(e) => setPriceDropEmail(e.target.value)}
                        placeholder={t.priceDropEmail}
                        className="flex-1 px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                      />
                      <button type="submit" className="btn-secondary text-sm whitespace-nowrap flex items-center gap-1">
                        <BellRing size={14} />
                        {t.priceDropNotify}
                      </button>
                    </form>
                  )}
                  {priceDropSubmitted && (
                    <p className="text-sm text-green-600 flex items-center gap-1"><BellRing size={14} /> {t.priceDropSuccess}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trust Badge Section */}
        <div className="mt-16 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold mb-3">Buy with Confidence</h3>
          <p className="text-gray-700 mb-4">
            {language === 'tr' ? 'Gerçek eBay geri bildirimlerimizi inceleyin' : 'Check our real eBay feedback'}
          </p>
          <a
            href="https://www.ebay.com/usr/northcyprusphilately"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <span>{language === 'tr' ? 'eBay Mağazamızı Görüntüle' : 'View Our eBay Store'}</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </div>

        {/* Customer Reviews */}
        <div className="mt-16">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold">{t.reviews}</h2>
              {reviewStats.total > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={16} className={s <= Math.round(reviewStats.average_rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
                    ))}
                  </div>
                  <span className="text-sm text-gray-600">{reviewStats.average_rating} ({reviewStats.total})</span>
                </div>
              )}
            </div>
            {!showReviewForm && (
              <button onClick={() => setShowReviewForm(true)} className="btn-secondary text-sm">
                {t.writeReview}
              </button>
            )}
          </div>

          {showReviewForm && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-1">{t.rating}</label>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(s => (
                    <button key={s} onClick={() => setReviewData({...reviewData, rating: s})}>
                      <Star size={24} className={s <= reviewData.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 hover:text-yellow-300'} />
                    </button>
                  ))}
                </div>
              </div>
              <input
                type="text"
                placeholder={t.reviewTitle}
                value={reviewData.title}
                onChange={(e) => setReviewData({...reviewData, title: e.target.value})}
                className="w-full px-3 py-2 border rounded mb-3 text-sm focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                maxLength={100}
              />
              <textarea
                placeholder={t.reviewComment}
                value={reviewData.comment}
                onChange={(e) => setReviewData({...reviewData, comment: e.target.value})}
                className="w-full px-3 py-2 border rounded mb-3 text-sm focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                rows={3}
                maxLength={1000}
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSubmitReview}
                  disabled={submittingReview || !reviewData.comment.trim()}
                  className="btn-primary text-sm disabled:opacity-50"
                >
                  {t.submitReview}
                </button>
                <button onClick={() => setShowReviewForm(false)} className="text-sm text-gray-500 hover:text-gray-700">
                  {t.cancel}
                </button>
              </div>
            </div>
          )}

          {reviews.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t.noReviews}</p>
          ) : (
            <div className="space-y-4">
              {reviews.map(review => (
                <div key={review.id} className="border-b pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {review.user_picture && (
                        <img src={review.user_picture} alt="" className="w-8 h-8 rounded-full" />
                      )}
                      <div>
                        <span className="font-semibold text-sm">{review.user_name}</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} size={12} className={s <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{new Date(review.created_at).toLocaleDateString()}</span>
                      {user && (user.id === review.user_id || user.role === 'admin') && (
                        <button onClick={() => handleDeleteReview(review.id)} className="text-red-400 hover:text-red-600" title={t.deleteReview}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  {review.title && <h4 className="font-semibold mt-2">{review.title}</h4>}
                  <p className="text-gray-700 text-sm mt-1">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {boughtTogether.length > 0 && (
          <div className="mt-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-2">{t.boughtTogether}</h2>
              <p className="text-gray-600">{t.boughtTogetherDesc}</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex flex-wrap items-center justify-center gap-4">
                {/* Current product */}
                <div className="text-center w-32">
                  {firstImage && <img src={firstImage} alt={displayTitle} className="w-28 h-28 object-cover rounded-lg mx-auto mb-2" />}
                  <p className="text-xs font-semibold truncate">{displayTitle}</p>
                  <p className="text-sm font-bold" style={{ color: '#B8941E' }}>${product.price?.toFixed(2)}</p>
                </div>
                {boughtTogether.map((bp) => {
                  const bpImg = bp.images?.[0] ? (bp.images[0].startsWith('http') ? bp.images[0] : `${BACKEND_URL}${bp.images[0]}`) : '';
                  const bpTitle = bp[`title_${language}`] || bp.title;
                  return (
                    <React.Fragment key={bp.id}>
                      <span className="text-2xl text-gray-300 font-light">+</span>
                      <Link to={productUrl(bp)} className="text-center w-32 block">
                        {bpImg && <img src={bpImg} alt={bpTitle} className="w-28 h-28 object-cover rounded-lg mx-auto mb-2 hover:ring-2 hover:ring-[#C8A951] transition" loading="lazy" />}
                        <p className="text-xs font-semibold truncate">{bpTitle}</p>
                        <p className="text-sm font-bold" style={{ color: '#B8941E' }}>${bp.price?.toFixed(2)}</p>
                      </Link>
                    </React.Fragment>
                  );
                })}
                <span className="text-2xl text-gray-300 font-light">=</span>
                <div className="text-center bg-green-50 rounded-xl p-4 min-w-[140px]">
                  <p className="text-xs text-gray-500 mb-1">{t.boughtTogether}</p>
                  <p className="text-xl font-bold" style={{ color: '#B8941E' }}>
                    ${(product.price + boughtTogether.reduce((s, p) => s + (p.price || 0), 0)).toFixed(2)}
                  </p>
                  <button
                    onClick={async () => {
                      try {
                        await Promise.all(boughtTogether.map(bp =>
                          axios.post(`${API}/cart/add`, null, { params: { product_id: bp.id, quantity: 1 }, withCredentials: true })
                        ));
                        await fetchCart();
                        toast.success(language === 'tr' ? 'Tümü sepete eklendi!' : 'All items added to cart!');
                      } catch { toast.error('Failed to add items'); }
                    }}
                    className="mt-2 bg-[#C8A951] hover:bg-[#b89842] text-white text-xs font-semibold py-2 px-4 rounded-lg transition"
                  >
                    {t.addAllToCart}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-2">{t.youMayLike}</h2>
              <p className="text-gray-600">{t.youMayLikeDesc}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedProducts.map(item => {
                const itemTitle = item[`title_${language}`] || item.title;
                const itemImage = item.images && item.images.length > 0
                  ? (item.images[0].startsWith('http') ? item.images[0] : `${BACKEND_URL}${item.images[0]}`)
                  : '';

                return (
                  <Link
                    key={item.id}
                    to={productUrl(item)}
                    className="product-card cursor-pointer block"
                  >
                    {itemImage && (
                      <div className="relative">
                        <img
                          src={itemImage}
                          alt={`${itemTitle} - ${item.condition || 'collectible'} TRNC stamp`}
                          loading="lazy"
                          width="300"
                          height="200"
                          className="w-full h-48 object-cover rounded-t-lg"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                          }}
                        />
                        {item.discount_rate > 0 && (
                          <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                            -{item.discount_rate}%
                          </span>
                        )}
                        {item.stock > 0 && item.stock <= 3 && (
                          <span className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded animate-pulse">
                            Only {item.stock} left!
                          </span>
                        )}
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold mb-2 line-clamp-2">{itemTitle}</h3>
                      {item.discounted_price && item.discounted_price < item.price ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[#B8941E] font-bold">
                            {item.currency} {item.discounted_price.toFixed(2)}
                          </span>
                          <span className="text-sm text-gray-400 line-through">
                            {item.currency} {item.price.toFixed(2)}
                          </span>
                        </div>
                      ) : (
                        <p className="text-[#B8941E] font-bold">
                          {item.currency} {item.price.toFixed(2)}
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Recently Viewed Products */}
        {recentlyViewed.length > 0 && (
          <div className="mt-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-2">{t.recentlyViewed}</h2>
              <p className="text-gray-600">{t.recentlyViewedDesc}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {recentlyViewed.map(item => {
                const itemTitle = item[`title_${language}`] || item.title;
                const itemImage = item.images && item.images.length > 0
                  ? (item.images[0].startsWith('http') ? item.images[0] : `${BACKEND_URL}${item.images[0]}`)
                  : '';

                return (
                  <Link
                    key={item.id || item._id}
                    to={productUrl(item)}
                    className="product-card cursor-pointer block"
                  >
                    {itemImage && (
                      <div className="relative">
                        <img
                          src={itemImage}
                          alt={`${itemTitle} - TRNC stamp`}
                          loading="lazy"
                          width="300"
                          height="160"
                          className="w-full h-40 object-cover rounded-t-lg"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                          }}
                        />
                        {item.discount_rate > 0 && (
                          <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                            -{item.discount_rate}%
                          </span>
                        )}
                      </div>
                    )}
                    <div className="p-3">
                      <h3 className="font-semibold text-sm mb-1 line-clamp-2">{itemTitle}</h3>
                      {item.discounted_price && item.discounted_price < item.price ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[#B8941E] font-bold text-sm">
                            {item.currency} {item.discounted_price.toFixed(2)}
                          </span>
                          <span className="text-xs text-gray-400 line-through">
                            {item.currency} {item.price.toFixed(2)}
                          </span>
                        </div>
                      ) : (
                        <p className="text-[#B8941E] font-bold text-sm">
                          {item.currency} {item.price?.toFixed(2)}
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Address Modal for Buy Now */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">{t.addressRequired}</h3>
            <p className="text-gray-600 mb-4 text-sm">
              {t.addressRequiredDesc}
            </p>
            
            <div className="space-y-3">
              <input
                type="text"
                placeholder={`${t.fullName} *`}
                value={addressData.full_name}
                onChange={(e) => setAddressData({...addressData, full_name: e.target.value})}
                className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                required
              />
              <input
                type="email"
                placeholder={`${t.email} *`}
                value={addressData.email}
                onChange={(e) => setAddressData({...addressData, email: e.target.value})}
                className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                required
              />
              <input
                type="text"
                placeholder={`${t.address} *`}
                value={addressData.address}
                onChange={(e) => setAddressData({...addressData, address: e.target.value})}
                className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                required
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder={`${t.city} *`}
                  value={addressData.city}
                  onChange={(e) => setAddressData({...addressData, city: e.target.value})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                  required
                />
                <input
                  type="text"
                  placeholder={t.zipCode}
                  value={addressData.zip_code}
                  onChange={(e) => setAddressData({...addressData, zip_code: e.target.value})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                />
              </div>
              <input
                type="text"
                placeholder={`${t.country} *`}
                value={addressData.country}
                onChange={(e) => setAddressData({...addressData, country: e.target.value})}
                className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                required
              />
              <input
                type="tel"
                placeholder={t.phone}
                value={addressData.phone}
                onChange={(e) => setAddressData({...addressData, phone: e.target.value})}
                className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
              />
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddressModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleAddressSaveAndContinue}
                disabled={!addressData.full_name || !addressData.email || !addressData.address || !addressData.city || !addressData.country}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.saveAndContinue}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
